import { MarketingEntryPoint } from "@redotech/redo-model/advanced-flow/schemas/marketing/marketing";
import {
  CustomFieldType,
  InputElement,
  MarketingForgeElementType,
} from "@redotech/redo-model/content-builder/element-schema";
import {
  CustomerCustomFieldDataType,
  CustomerCustomFieldValue,
} from "@redotech/redo-model/customer-custom-fields/customer-custom-fields-definition";
import { SignupFormData } from "@redotech/redo-model/marketing/schemas/submit-signup-form-step-schema";
import {
  customFieldTypeToDataType,
  URLMatchingType,
  URLVisibilityType,
} from "@redotech/redo-model/signup-form";
import { SignupForm } from "@redotech/redo-model/signup-form-schema";
import { useCallback, useEffect, useState } from "react";
import { ANONYMOUS_SHOPPER_ID_KEY } from "../../utils/shared-conf";
import { SignupFormClient, SignupFormLocalData } from "./types";

export const EXPIRATION_PERIOD = 24 * 60 * 60 * 1000;

export function isOnMobile(): boolean {
  if ((navigator as any).userAgentData?.mobile) return true;
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
  )
    return true;
  return innerWidth < 768;
}

export function generateAnonymousCustomerId() {
  return `anonymous-${Math.random().toString(36).substring(2)}`;
}

export function parseLocalData(data: string): SignupFormLocalData {
  try {
    const raw = JSON.parse(data);
    return {
      customerHash: raw.customerHash,
      formAutoOpenedAt: raw.formAutoOpenedAt
        ? new Date(raw.formAutoOpenedAt)
        : undefined,
      formViewedAt: raw.formViewedAt ? new Date(raw.formViewedAt) : undefined,
      atLeastOneStepSubmittedAt: raw.atLeastOneStepSubmittedAt
        ? new Date(raw.atLeastOneStepSubmittedAt)
        : undefined,
      formId: raw.formId,
    };
  } catch (e) {
    console.error("Failed to parse signup form localStorage", e);
    return {};
  }
}

export const getNextStepIndex = (
  form: SignupForm | null,
  skipPhoneSteps: boolean,
  startIndex: number,
): number => {
  if (!form) return startIndex;
  let stepIndex = startIndex;

  while (
    skipPhoneSteps &&
    stepIndex < form.steps.length &&
    form.steps[stepIndex]?.elements.some((el) => {
      // Skip phone inputs but process column elements
      if (el.type === MarketingForgeElementType.COLUMN) {
        // Check nested elements in columns for phone inputs
        return el.elements.some(
          (colEl) =>
            colEl.type === MarketingForgeElementType.INPUT &&
            colEl.inputType === "phone",
        );
      }
      return (
        el.type === MarketingForgeElementType.INPUT && el.inputType === "phone"
      );
    })
  ) {
    stepIndex++;
  }

  return stepIndex;
};

export function useSignupFormAnalytics({
  client,
  formId,
  treatmentId,
  shopifyCustomerId,
  localData,
  setLocalData,
}: {
  client: SignupFormClient | undefined;
  formId: string | null;
  treatmentId: string | null;
  shopifyCustomerId: string | null;
  localData: SignupFormLocalData;
  setLocalData: (
    value:
      | SignupFormLocalData
      | ((prev: SignupFormLocalData) => SignupFormLocalData),
  ) => void;
}) {
  const logAnalytics = useCallback(
    async (type: "view" | "submit") => {
      if (!client || !formId) return;

      const anonymousId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);

      if (anonymousId) {
        await client.logSignupFormAnalytics({
          anonymousId,
          formId,
          treatmentId,
          type,
          shopifyCustomerId,
        });
      }

      if (type === "view") {
        setLocalData((prev: SignupFormLocalData) => ({
          ...prev,
          formViewedAt: new Date(),
        }));
      } else {
        setLocalData((prev: SignupFormLocalData) => ({
          ...prev,
          atLeastOneStepSubmittedAt: new Date(),
        }));
      }
    },
    [client, formId, setLocalData, shopifyCustomerId, treatmentId],
  );

  return { logAnalytics };
}

export const submitFormStep = async ({
  i,
  formData,
  form,
  formId,
  treatmentId,
  client,
  customerId,
  localData,
  logAnalytics,
  setCurrentStep,
  entryPoint,
  skipPhoneSteps,
  onLastStepSubmitted,
}: {
  i: number;
  formData: Record<string, string>;
  form: SignupForm | null;
  formId: string | null;
  treatmentId: string | null;
  client: SignupFormClient | undefined;
  customerId?: string | null;
  localData: SignupFormLocalData;
  logAnalytics: (type: "view" | "submit") => Promise<void>;
  setCurrentStep: (step: number) => void;
  entryPoint: MarketingEntryPoint;
  skipPhoneSteps: boolean;
  onLastStepSubmitted?: () => void;
}): Promise<{ success: boolean; discountCode?: string }> => {
  if (!form || !formId || !client) return { success: false };

  const transformedFormData: SignupFormData = {};
  const customFields: {
    declarationId: string;
    dataType: CustomerCustomFieldDataType;
    value: CustomerCustomFieldValue;
  }[] = [];

  for (const [key, value] of Object.entries(formData)) {
    if (key === "email" || key === "phone") {
      transformedFormData[key] = value;
    } else if (key === "birthday") {
      const [month, day, year] = value.split("-").map((x) => parseInt(x));
      transformedFormData.birthday = new Date(
        Temporal.PlainDate.from({ year, month, day }).toString(),
      );
    }
  }

  const processCustomFieldInputElement = (
    element: InputElement,
    formData: Record<string, string>,
    customFields: Array<{
      declarationId: string;
      dataType: CustomerCustomFieldDataType;
      value: CustomerCustomFieldValue;
    }>,
  ) => {
    if (!element.customField) return;
    const rawValue = formData[element.customField.id];
    let processedValue: CustomerCustomFieldValue = rawValue;
    switch (element.customField.fieldType) {
      case CustomFieldType.Number:
        processedValue = parseFloat(rawValue);
        break;
      case CustomFieldType.Date:
        try {
          const [month, day, year] = rawValue
            .split("-")
            .map((x) => parseInt(x));
          processedValue = new Date(
            Temporal.PlainDate.from({ year, month, day }).toString(),
          );
        } catch (e) {
          console.error("Failed to parse date for custom field", e);
          processedValue = rawValue;
        }
        break;
      case CustomFieldType.YesNoQuestion:
        processedValue =
          rawValue.toLowerCase() === "yes" || rawValue.toLowerCase() === "true";
        break;
      // For text, text-short, and dropdown, use the raw value
      default:
        processedValue = rawValue;
    }

    customFields.push({
      declarationId: element.customField.id,
      dataType: customFieldTypeToDataType[element.customField.fieldType],
      value: processedValue,
    });
  };

  const currentStep = form.steps[i];
  if (currentStep) {
    for (const element of currentStep.elements) {
      if (element.type === MarketingForgeElementType.COLUMN) {
        for (const columnElement of element.elements) {
          if (
            columnElement.type === MarketingForgeElementType.INPUT &&
            columnElement.customField &&
            columnElement.customField.id &&
            formData[columnElement.customField.id]
          ) {
            processCustomFieldInputElement(
              columnElement,
              formData,
              customFields,
            );
          }
        }
      } else if (
        element.type === MarketingForgeElementType.INPUT &&
        element.customField &&
        element.customField.id &&
        formData[element.customField.id]
      ) {
        processCustomFieldInputElement(element, formData, customFields);
      }
    }
  }

  if (customFields.length > 0) {
    transformedFormData.customFields = customFields;
  }

  const result = await client.submitSignupFormStep({
    formId,
    stepIndex: i,
    formData: transformedFormData,
    customerId,
    entryPoint,
    anonymousId: localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY)!,
    treatmentId,
  });

  if (!result.success) {
    console.error("Failed to submit form step", result);
    return { success: false };
  }

  if (!localData.atLeastOneStepSubmittedAt && localData.customerHash) {
    await logAnalytics("submit");
  }

  const nextStepIndex = getNextStepIndex(form, skipPhoneSteps, i + 1);
  const hasNextStep = nextStepIndex < form.steps.length;

  if (hasNextStep) {
    setCurrentStep(nextStepIndex);
  } else {
    setCurrentStep(0);
    onLastStepSubmitted?.();
  }

  return { success: true, discountCode: result.discountCode };
};

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    const item = localStorage.getItem(key);
    return item ? (parseLocalData(item) as T) : initialValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(storedValue));
  }, [key, storedValue]);

  return [storedValue, setStoredValue];
}

export function isValidURLtoShowForm(form: SignupForm | null): boolean {
  if (!form?.behavior.urlVisibility) return true;

  const { type, urls, matchingType } = form.behavior.urlVisibility;
  const currentUrl = location.href;

  const matchesUrl = urls.some((url) =>
    matchingType === URLMatchingType.EXACT
      ? currentUrl === url
      : currentUrl.toLowerCase().includes(url.toLowerCase()),
  );

  return type === URLVisibilityType.INCLUSIVE ? matchesUrl : !matchesUrl;
}

export function getOpacityHex(form: SignupForm | null): string {
  if (!form) return "00";
  return Math.floor((form.theme.overlay.opacityPercent / 100) * 255)
    .toString(16)
    .padStart(2, "0");
}
